import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, ProgressBar, InputGroup, Form, Dropdown } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import './StockCritique.css';

export default function StockCritique() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  useEffect(() => {
    fetchStockCritique();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchStockCritique, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchStockCritique = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `${API_BASE_URL}/stock_critique.php`,
        {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/pages/signin2");
          throw new Error("Session expirée. Veuillez vous reconnecter.");
        }
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error("Erreur de chargement:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchStockCritique();
  };

  const handleCreateOrder = (produit) => {
    navigate(`/achats/commandesNouveau?produit=${produit.id_produit}&quantite=${produit.quantite_manquante}`);
  };

  const handleViewProduct = (produitId) => {
    navigate(`/stock/produits/${produitId}`);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    if (newSelectAll) {
      setSelectedProducts(filteredProduits.map(p => p.id_produit));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (produitId) => {
    if (selectedProducts.includes(produitId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== produitId));
    } else {
      setSelectedProducts([...selectedProducts, produitId]);
    }
  };

  const handleBulkOrder = () => {
    const produitsSelectionnes = filteredProduits.filter(p => selectedProducts.includes(p.id_produit));
    if (produitsSelectionnes.length > 0) {
      const produitsData = produitsSelectionnes.map(p => ({
        id: p.id_produit,
        quantite: p.quantite_manquante || 1,
        designation: p.designation,
        reference: p.reference
      }));
      navigate('/achats/commandesNouveau', { 
        state: { 
          produits: produitsData,
          type: 'bulk_order' 
        } 
      });
    }
  };

  const filteredProduits = data?.produits?.filter(produit => {
    const matchesSearch = produit.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         produit.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || produit.niveau_alerte === filter;
    const matchesCategory = selectedCategory === 'all' || produit.nom_categorie === selectedCategory;
    return matchesSearch && matchesFilter && matchesCategory;
  }) || [];

  const categories = [...new Set(data?.produits?.map(p => p.nom_categorie) || [])].filter(Boolean);

  const getNiveauBadge = (niveau) => {
    const badges = {
      'rupture': { bg: '#dc3545', text: 'Rupture', icon: 'ri-alert-line', bgSoft: '#f8d7da' },
      'critique': { bg: '#fd7e14', text: 'Critique', icon: 'ri-alert-line', bgSoft: '#fff3e0' },
      'alerte': { bg: '#0dcaf0', text: 'Alerte', icon: 'ri-information-line', bgSoft: '#e2f3f5' }
    };
    return badges[niveau] || badges.alerte;
  };

  const seriesCategories = {
    series: data?.categories?.map(c => c.nb_produits) || [],
    options: {
      chart: {
        type: 'donut',
        height: 320,
        toolbar: { show: false },
        fontFamily: 'Inter, system-ui, sans-serif'
      },
      labels: data?.categories?.map(c => c.nom_categorie || 'Sans catégorie') || [],
      colors: ['#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899'],
      legend: { position: 'bottom', fontSize: '11px', offsetY: 5 },
      tooltip: { y: { formatter: (value) => `${value} produit(s)` } },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                fontSize: '14px',
                fontWeight: 600,
                formatter: () => data?.produits?.length || 0
              }
            }
          }
        }
      },
      dataLabels: { style: { fontSize: '11px', fontWeight: 500 } }
    }
  };

  const seriesHistorique = [{
    name: 'Alertes',
    data: data?.historique?.map(h => h.nb_alertes).reverse() || []
  }];

  const optionsHistorique = {
    chart: {
      type: 'area',
      height: 250,
      toolbar: { show: false },
      fontFamily: 'Inter, system-ui, sans-serif'
    },
    colors: ['#ef4444'],
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1 }
    },
    stroke: { curve: 'smooth', width: 2 },
    grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
    xaxis: {
      categories: data?.historique?.map(h => {
        const date = new Date(h.date_alerte);
        return `${date.getDate()}/${date.getMonth() + 1}`;
      }).reverse() || [],
      labels: { style: { fontSize: '11px', fontWeight: 500 } }
    },
    yaxis: {
      title: { text: 'Nombre d\'alertes', style: { fontSize: '11px', fontWeight: 500 } },
      labels: { style: { fontSize: '11px' } }
    },
    tooltip: { y: { formatter: (value) => `${value} alerte(s)` } }
  };

  if (loading && !data) {
    return (
        <>
          <Header />
          <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
              <div className="text-center">
                {/* Logo animé */}
                <div className="loading-animation mb-4">
                  <div className="loading-ring">
                    <div className="loading-ring-inner"></div>
                  </div>
                  <i className="ri-error-warning-line loading-icon"></i>
                </div>
                
                {/* Spinner élégant */}
             
                
                <h5 className="fw-bold text-primary mb-2">Chargement des stocks critiques</h5>
                <p className="text-muted small mb-0">Récupération des données en cours...</p>
                
                {/* Barre de progression animée */}
                <div className="mx-auto mt-4" style={{ width: '200px' }}>
                  <div className="progress" style={{ height: '3px', borderRadius: '3px', background: '#e9ecef' }}>
                    <div 
                      className="progress-bar progress-bar-striped progress-bar-animated" 
                      style={{ width: '100%', background: 'linear-gradient(90deg, #4361ee, #10b981, #f59e0b)' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
          
          <style>{`
            .loading-animation {
              position: relative;
              width: 80px;
              height: 80px;
              margin: 0 auto;
            }
            .loading-ring {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              border-radius: 50%;
              background: linear-gradient(135deg, #4361ee, #10b981);
              animation: pulse-ring 1.5s ease-in-out infinite;
            }
            .loading-ring-inner {
              position: absolute;
              top: 5px;
              left: 5px;
              right: 5px;
              bottom: 5px;
              border-radius: 50%;
              background: white;
            }
            .loading-icon {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              font-size: 28px;
              color: #4361ee;
              z-index: 2;
              animation: pulse-icon 1.5s ease-in-out infinite;
            }
            @keyframes pulse-ring {
              0% { transform: scale(0.9); opacity: 0.7; }
              50% { transform: scale(1.1); opacity: 0.3; }
              100% { transform: scale(0.9); opacity: 0.7; }
            }
            @keyframes pulse-icon {
              0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
              50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; }
            }
          `}</style>
        </>
      );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <Alert variant="danger" className="border-0 shadow-sm rounded-0">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={handleRefresh}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  const hasCriticalStock = (data?.statistiques?.nb_ruptures > 0 || data?.statistiques?.nb_critiques > 0);
  const selectedCount = selectedProducts.length;

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
        
        {/* En-tête moderne */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="p-2 bg-danger bg-opacity-10 rounded-0">
                <i className="ri-alert-line fs-20 text-danger"></i>
              </div>
              <div>
                <h4 className="main-title mb-0 fw-bold">
                  Surveillance du stock critique
                  {hasCriticalStock && (
                    <Badge bg="danger" className="ms-2 rounded-pill">
                      {data?.statistiques?.nb_ruptures || 0} rupture{(data?.statistiques?.nb_ruptures || 0) > 1 ? 's' : ''}
                    </Badge>
                  )}
                </h4>
                <p className="text-muted small mb-0">Gestion proactive des stocks et réapprovisionnement</p>
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="form-check form-switch d-flex align-items-center ms-2">
              <input
                className="form-check-input me-2"
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                id="autoRefresh"
              />
              <label className="form-check-label small text-muted" htmlFor="autoRefresh">
                Auto-refresh
              </label>
            </div>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="d-flex align-items-center gap-1 rounded-pill px-3"
            >
              <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i>
              Actualiser
            </Button>
            <Dropdown align="end">
              <Dropdown.Toggle variant="success" size="sm" className="rounded-pill px-3 d-flex align-items-center gap-1" disabled={selectedCount === 0}>
                <i className="ri-shopping-cart-line"></i>
                Commander ({selectedCount})
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleBulkOrder}>
                  <i className="ri-shopping-cart-line text-success me-2"></i>
                  Commander la sélection
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* KPI Cards modernes */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <div className="kpi-card kpi-danger">
              <div className="kpi-icon">
                <i className="ri-alert-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Rupture de stock</span>
                <h3 className="kpi-value">{data?.statistiques?.nb_ruptures || 0}</h3>
                <span className="kpi-unit">produits</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-warning">
              <div className="kpi-icon">
                <i className="ri-alert-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Stock critique</span>
                <h3 className="kpi-value">{data?.statistiques?.nb_critiques || 0}</h3>
                <span className="kpi-unit">produits</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-success">
              <div className="kpi-icon">
                <i className="ri-information-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Stock en alerte</span>
                <h3 className="kpi-value">{data?.statistiques?.nb_alertes || 0}</h3>
                <span className="kpi-unit">produits</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-primary">
              <div className="kpi-icon">
                <i className="ri-money-euro-circle-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Valeur réappro.</span>
                <h3 className="kpi-value">{formatCurrency(data?.statistiques?.valeur_reapprovisionnement || 0)}</h3>
                <span className="kpi-unit">investissement nécessaire</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Graphiques */}
        <Row className="g-3 mb-4">
          <Col xl="4">
            <Card className="border-0 shadow-sm rounded-0 h-100">
              <Card.Header className="bg-transparent border-0 pt-3 px-4">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-pie-chart-2-line text-primary"></i>
                  <h6 className="mb-0 fw-semibold">Répartition par catégorie</h6>
                </div>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                {data?.categories?.length > 0 ? (
                  <>
                    <ReactApexChart series={seriesCategories.series} options={seriesCategories.options} type="donut" height={300} />
                    <div className="mt-3">
                      {data?.categories?.slice(0, 5).map((cat, idx) => (
                        <div key={idx} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <span className="small fw-semibold">{cat.nom_categorie || 'Sans catégorie'}</span>
                          <Badge bg="secondary" className="rounded-pill">{cat.nb_produits}</Badge>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i className="ri-pie-chart-2-line fs-48 text-muted mb-3 d-block"></i>
                    <p className="text-muted">Aucune catégorie concernée</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl="8">
            <Card className="border-0 shadow-sm rounded-0">
              <Card.Header className="bg-transparent border-0 pt-3 px-4">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-bar-chart-line text-danger"></i>
                  <h6 className="mb-0 fw-semibold">Historique des alertes (30 jours)</h6>
                </div>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <ReactApexChart series={seriesHistorique} options={optionsHistorique} type="area" height={250} />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtres */}
        <Card className="border-0 shadow-sm rounded-0 mb-4">
          <Card.Body className="py-3 px-4">
            <Row className="align-items-center g-3">
              <Col md="4">
                <div className="position-relative">
                  <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher un produit..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ps-5 rounded-pill"
                  />
                </div>
              </Col>
              <Col md="3">
                <Form.Select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-pill px-3">
                  <option value="all">📊 Tous les niveaux</option>
                  <option value="rupture">🔴 Rupture</option>
                  <option value="critique">🟠 Critique</option>
                  <option value="alerte">🔵 Alerte</option>
                </Form.Select>
              </Col>
              <Col md="3">
                <Form.Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="rounded-pill px-3">
                  <option value="all">📁 Toutes catégories</option>
                  {categories.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="2">
                <div className="text-end">
                  <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                    <i className="ri-stack-line me-1"></i>
                    {filteredProduits.length} produit(s)
                  </span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des produits */}
        <Card className="border-0 shadow-sm rounded-0 mb-4">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: '40px' }} className="ps-4">
                      <Form.Check 
                        type="checkbox"
                        checked={selectAll && filteredProduits.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Référence</th>
                    <th>Produit</th>
                    <th className="text-end">Stock actuel</th>
                    <th className="text-end">Seuil</th>
                    <th className="text-end">Manquant</th>
                    <th>Emplacement</th>
                    <th>Niveau</th>
                    <th className="text-end pe-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProduits.length > 0 ? (
                    filteredProduits.map((produit) => {
                      const badge = getNiveauBadge(produit.niveau_alerte);
                      const manquant = produit.seuil_alerte - produit.quantite_reelle;
                      const pourcentage = Math.min((produit.quantite_reelle / produit.seuil_alerte) * 100, 100);
                      
                      return (
                        <tr key={produit.id_produit} className={produit.niveau_alerte === 'rupture' ? 'table-danger' : ''}>
                          <td className="ps-4">
                            <Form.Check 
                              type="checkbox"
                              checked={selectedProducts.includes(produit.id_produit)}
                              onChange={() => handleSelectProduct(produit.id_produit)}
                            />
                          </td>
                          <td>
                            <span className="reference-badge">{produit.reference}</span>
                          </td>
                          <td>
                            <div className="fw-semibold">{produit.designation}</div>
                            {produit.code_barre && (
                              <small className="text-muted font-monospace">{produit.code_barre}</small>
                            )}
                          </td>
                          <td className="text-end">
                            <div className="d-flex align-items-center justify-content-end gap-2">
                              <span className={`fw-bold ${produit.quantite_reelle <= 0 ? 'text-danger' : ''}`}>
                                {formatNumber(produit.quantite_reelle)}
                              </span>
                              <div className="progress-bar-custom" style={{ width: '70px', height: '5px' }}>
                                <div className="progress-fill-custom" style={{ width: `${pourcentage}%`, background: pourcentage <= 0 ? '#dc3545' : pourcentage <= 30 ? '#fd7e14' : '#0dcaf0' }}></div>
                              </div>
                            </div>
                          </td>
                          <td className="text-end">{formatNumber(produit.seuil_alerte)}</td>
                          <td className="text-end">
                            {manquant > 0 ? (
                              <span className="text-danger fw-bold">{formatNumber(manquant)}</span>
                            ) : '-'}
                          </td>
                          <td>{produit.emplacement || '-'}</td>
                          <td>
                            <span className="status-badge" style={{ background: `${badge.bgSoft}`, color: badge.bg }}>
                              <i className={`${badge.icon} me-1`} style={{ fontSize: '12px' }}></i>
                              {badge.text}
                            </span>
                          </td>
                          <td className="text-end pe-4">
                            <div className="d-flex gap-2 justify-content-end">
                              <button 
                                className="action-icon"
                                onClick={() => handleViewProduct(produit.id_produit)}
                                title="Voir le produit"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                              <button 
                                className="action-icon action-icon-success"
                                onClick={() => handleCreateOrder(produit)}
                                title="Créer une commande"
                              >
                                <i className="ri-shopping-cart-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <i className="ri-checkbox-circle-line fs-48 text-success mb-3 d-block"></i>
                        <h5 className="mb-1">Aucun produit en stock critique</h5>
                        <p className="text-muted small mb-0">Tous les stocks sont à des niveaux acceptables</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* Suggestions */}
        {data?.suggestions?.length > 0 && (
          <Card className="border-0 shadow-sm rounded-0">
            <Card.Header className="bg-transparent border-0 pt-3 px-4">
              <div className="d-flex align-items-center gap-2">
                <i className="ri-lightbulb-line text-warning"></i>
                <h6 className="mb-0 fw-semibold">Suggestions de réapprovisionnement</h6>
              </div>
            </Card.Header>
            <Card.Body className="px-4 pb-4">
              <Row className="g-3">
                {data.suggestions.slice(0, 3).map((suggestion, idx) => (
                  <Col md="4" key={idx}>
                    <div className="suggestion-card">
                      <div className="suggestion-header">
                        <span className="reference-badge">{suggestion.reference}</span>
                        <i className="ri-shopping-cart-line fs-20 text-primary"></i>
                      </div>
                      <h6 className="fw-semibold mt-2 mb-1">{suggestion.designation}</h6>
                      <small className="text-muted d-block mb-3">{suggestion.fournisseur || 'Fournisseur non spécifié'}</small>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted small">À commander:</span>
                        <span className="fw-bold text-danger">{formatNumber(suggestion.quantite_a_commander)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-3">
                        <span className="text-muted small">Coût total:</span>
                        <span className="fw-bold text-primary">{formatCurrency(suggestion.cout_total)}</span>
                      </div>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="w-100 rounded-pill"
                        onClick={() => handleCreateOrder(suggestion)}
                      >
                        <i className="ri-shopping-cart-line me-2"></i>
                        Commander
                      </Button>
                    </div>
                  </Col>
                ))}
              </Row>
              {data.suggestions.length > 3 && (
                <div className="text-center mt-4">
                  <Link to="/achats/commandes/suggestions" className="text-decoration-none">
                    Voir toutes les suggestions ({data.suggestions.length}) <i className="ri-arrow-right-line ms-1"></i>
                  </Link>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>

      <style>{`
        /* KPI Cards */
        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-danger { border-left-color: #dc3545; }
        .kpi-warning { border-left-color: #fd7e14; }
        .kpi-info { border-left-color: #0dcaf0; }
        .kpi-primary { border-left-color: #4361ee; }
        .kpi-success { border-left-color: #3EA436; }
        
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-danger .kpi-icon { background: #f8d7da; color: #dc3545; }
        .kpi-warning .kpi-icon { background: #fff3e0; color: #fd7e14; }
        .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
        .kpi-primary .kpi-icon { background: #e8f0fe; color: #4361ee; }
        
        .kpi-content {
          flex: 1;
        }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.6rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .kpi-unit {
          font-size: 0.7rem;
          color: #6c757d;
        }
        
        /* Status Badge */
        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        /* Reference Badge */
        .reference-badge {
          background: #f8f9fa;
          padding: 0.2rem 0.6rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-family: monospace;
        }
        
        /* Progress Bar Custom */
        .progress-bar-custom {
          background: #e9ecef;
          border-radius: 10px;
          overflow: hidden;
        }
        .progress-fill-custom {
          height: 100%;
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        
        /* Action Icons */
        .action-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          color: #6c757d;
          transition: all 0.2s;
          cursor: pointer;
        }
        .action-icon:hover {
          background: #4361ee;
          border-color: #4361ee;
          color: white;
        }
        .action-icon-success:hover {
          background: #198754;
          border-color: #198754;
          color: white;
        }
        
        /* Suggestion Card */
        .suggestion-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 1rem;
          transition: all 0.2s;
          height: 100%;
        }
        .suggestion-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        .suggestion-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }
        
        .fs-14 { font-size: 0.875rem; }
        .font-monospace { font-family: 'Courier New', monospace; }
      `}</style>
    </>
  );
}